import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { AlertClassColour, ConfigSystemParams, msSize } from 'src/app/core/consts';
import { AuthenticationService } from 'src/app/core/services/auth.service';
import { catchError, firstValueFrom, throwError } from 'rxjs';
import { CompaniesService } from 'src/app/core/services/utelize/system/company/companies-service';
import { CompletedModalFormComponent } from 'src/app/forms-modal/_pop-ups/completed-modal-form/completed-modal-form.component';
import { ConfigSystemService } from 'src/app/core/services/utelize/system/config-system/config-system-service';
import { CountryGroupAllocationDTO } from 'src/app/core/models/system/country-group/country-group-allocation.dto';
import { CountryGroupDTO } from 'src/app/core/models/system/country-group/country-group.dto';
import { CountryGroupService } from 'src/app/core/services/utelize/system/country-group/country-group-service';
import { ExceptionsService } from 'src/app/core/services/utelize/general/exceptions-service';
import { FormControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { IdAndDescriptionDTO } from 'src/app/core/interfaces/general/id-description';
import { LastPassKillerService } from 'src/app/core/services/utelize/general/last-pass-killer-service';
import { ModalForceCloseService } from 'src/app/core/services/utelize/general/modal-force-close-service';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { PageCompHeaderComponent } from "src/app/core/page-components/page-comp-header/page-comp-header.component";
import { SystemUserDTO } from 'src/app/core/models/system/user/system-user.dto';

@Component({
  selector: 'app-country-groups-modal-edit-form',
  templateUrl: './country-groups-modal-edit-form.component.html',
  styleUrls: ['./country-groups-modal-edit-form.component.scss']
})

export class CountryGroupModalEditFormComponent implements AfterViewInit, OnDestroy, OnInit {

  @Input() initialCompanyId: number | undefined;

  @Output() countryGroupFormCancelled   : EventEmitter<void> = new EventEmitter<void>();
  @Output() countryGroupDataHasBeenSaved: EventEmitter<any>  = new EventEmitter<any>();

  @ViewChild('countryGroupModalFormContent', { static: false }) countryGroupModalFormContent! : TemplateRef<void>;
  @ViewChild('completedModalForm',           { static: false }) completedModalForm!            : CompletedModalFormComponent;
  @ViewChild(PageCompHeaderComponent                          ) pageHeader!                    : PageCompHeaderComponent;

  get modalBackGround() { let value: string | boolean=this.configSystemService.getValue(ConfigSystemParams.ModalBackGround, "TRUE");
                          return value=value.toUpperCase()=="STATIC" ? "static"     : (value.toUpperCase()=="TRUE" ? true : false) }

  private curUser                   : SystemUserDTO;
  private countryGroup              : CountryGroupDTO | undefined;
  private countryGroupModalFormRef! : NgbModalRef;
  private countryGroupSavedMessage  : string="Changes applied.";

  public alertColourProgress        : string=AlertClassColour.Grey;
  public companyList                : IdAndDescriptionDTO[] = [];
  public formSubmitted              : boolean=false;
  public formTitle                  : string="Country Group";
  public isUtelize                  : boolean=false;
  public readOnly                   : boolean=false;
  public countryGroupForm!          : UntypedFormGroup;
  public countryGroupId             : number=0;
  public sureSimCompanyId           : number | undefined;

  public nameMaxLength              : number=100;

  public allCountries               : CountryGroupAllocationDTO[] = [];
  public countrySearch              : string = '';
  public selected                   : Set<number> = new Set<number>();

  constructor(
    private authenticationService : AuthenticationService,
    private companiesService      : CompaniesService,
    private configSystemService   : ConfigSystemService,
    private countryGroupService   : CountryGroupService,
    private exceptionsService     : ExceptionsService,
    private formBuilder           : UntypedFormBuilder,
    private lastPassKillerService : LastPassKillerService,
    private modalForceCloseService: ModalForceCloseService,
    private modalService          : NgbModal
  ) {
    this.curUser   =this.authenticationService.currentUserObject();
    this.isUtelize =this.curUser.isUtelize;
  }

  public ngOnInit(): void {
    try {
      this.setUpForm();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public ngAfterViewInit(): void {
    try {
      this.lastPassKillerService.start();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public ngOnDestroy() {
    try {
      this.lastPassKillerService.stop();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public async openFormGroup(countryGroupId: number, companyId: number, readOnly: boolean = false): Promise<void> {
    try {
      this.sureSimCompanyId    = companyId;
      this.countryGroupId      = countryGroupId;
      this.readOnly            = readOnly;
      this.formTitle           = readOnly ? "View Country Group" : (countryGroupId > 0 ? "Edit Country Group" : "Create Country Group");
      this.formSubmitted       = false;
      this.alertColourProgress = AlertClassColour.Grey;
      this.countrySearch       = '';
      this.setUpForm();

      if (!readOnly && this.isUtelize) {
        this.companyList = await firstValueFrom(this.companiesService.getIdAndDescriptionForLoggedInUser(this.curUser.userId));
      }

      await this.getCountryGroupAndOpenModal(countryGroupId);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private async getCountryGroupAndOpenModal(countryGroupId: number): Promise<void> {
    try {
      if (countryGroupId > 0) {
        const data=await firstValueFrom(this.countryGroupService.getById(countryGroupId)
                                        .pipe(
                                          catchError(error => {
                                            throw new Error(this.exceptionsService.formatHttpError(error));
                                          })
                                        ));

        this.countryGroup   = data;
        this.countryGroupId = data.id;
        this.populateFormFields();

        this.loadCountries();
        this.openModal();
        return;
      }

      this.countryGroup = {
        active      : true,
        alertCount  : 0,
        companyID   : this.sureSimCompanyId,
        countryCount: 0,
        enteredBy   : -1,
        enteredByName: '',
        id          : -1,
        groupName   : '',
        allocations : [],
        allCustomers: false,
        systemOwned : false,
        owner       : '',
      };

      this.populateFormFields();

      this.loadCountries();
      this.openModal();
    }
    catch (e) {
      this.countryGroupFormCancelled.emit();
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private openModal(): void {
    try {
      this.countryGroupModalFormRef = this.modalService.open(this.countryGroupModalFormContent, {
        backdrop     : this.modalBackGround,
        centered     : true,
        keyboard     : false,
        size         : msSize.Large,
        beforeDismiss: () => { return this.modalForceCloseService.isForcing(); },
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private populateFormFields() {
    try {
      if (this.countryGroup != undefined) {
        this.countryGroupForm.controls["active"].setValue(this.countryGroup.active);
        this.countryGroupForm.controls["allCustomers"].setValue(this.countryGroup.companyID == undefined || this.countryGroup.companyID == null);
        this.countryGroupForm.controls["companyId"].setValue(this.countryGroup.companyID);
        this.countryGroupForm.controls["id"].setValue(this.countryGroup.id);
        this.countryGroupForm.controls["groupName"].setValue(this.countryGroup.groupName);
        this.countryGroupForm.controls["systemOwned"].setValue(this.countryGroup.systemOwned);
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  //#region countryGroupForm

  get formData() {
    return this.countryGroupForm.controls;
  }

  get formOfCountryGroupAdd() {
    return this.countryGroupForm.controls;
  }

  get allCustomersValue(): boolean {
    return this.countryGroupForm?.controls["allCustomers"]?.value == true;
  }

  private setUpForm() {
    try {
      this.removeFormFields(this.countryGroupForm);
      this.countryGroupForm=this.formBuilder.group({ });
      this.addFormFields(this.countryGroupForm);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private addFormFields(formGroup: UntypedFormGroup) {
    try {
      this.addFormControl(formGroup, "active",       "", []);
      this.addFormControl(formGroup, "allCustomers", "", []);
      this.addFormControl(formGroup, "companyId",    "", []);
      this.addFormControl(formGroup, "id",           "", []);
      this.addFormControl(formGroup, "groupName",    "", [Validators.required, Validators.maxLength(this.nameMaxLength)]);
      this.addFormControl(formGroup, "systemOwned",  "", []);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private addFormControl(form      : UntypedFormGroup,
                         dataField : string,
                         value     : string,
                         validators: Validators) {
    try {
      form.addControl(dataField, new FormControl(value, validators));
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private removeFormFields(form: UntypedFormGroup) {
    try {
      if (form != undefined) {
        for (let control in form.controls) {
          form.removeControl(control);
        }
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public setVisibility(allCustomers: boolean) {
    try {
      if (this.readOnly)
        return;

      this.countryGroupForm.controls["allCustomers"].setValue(allCustomers);

      if (allCustomers)
        this.countryGroupForm.controls["companyId"].setValue(null);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public countryGroupFormCancel() {
    try {
      this.countryGroupFormCancelled.emit();
      this.countryGroupModalFormRef.close();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public countryGroupCompletedModalHasBeenClosed() {
    try {
      this.countryGroupModalFormRef.close();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  get canSubmit(): boolean {
    if (this.readOnly)
      return false;

    const nameOk      = ((this.countryGroupForm?.controls["groupName"]?.value ?? "") as string).trim().length > 0;
    const countriesOk = this.selected.size > 0;
    const companyOk   = !this.isUtelize || this.allCustomersValue || !!this.countryGroupForm?.controls["companyId"]?.value;

    return nameOk && countriesOk && companyOk;
  }

  public countryGroupFormSubmitAttempt() {
    try {
      this.formSubmitted=true;

      if (this.countryGroupForm.valid && this.canSubmit) {
        this.prepareToSaveCountryGroup();
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private prepareToSaveCountryGroup() {
    try {
      if (this.countryGroup != undefined)
      {
        const allCustomers = this.countryGroupForm.controls["allCustomers"].value == true;

        this.countryGroup.active      = this.countryGroupForm.controls["active"].value ?? true;
        this.countryGroup.enteredBy   = this.curUser.userId;
        this.countryGroup.id          = this.countryGroupForm.controls["id"].value;
        this.countryGroup.groupName   = this.countryGroupForm.controls["groupName"].value;

        if (this.isUtelize) {
          this.countryGroup.systemOwned = true;
          this.countryGroup.companyID   = allCustomers ? undefined : this.countryGroupForm.controls["companyId"].value;
        } else {
          this.countryGroup.systemOwned = false;
          this.countryGroup.companyID   = this.sureSimCompanyId;
        }

        this.countryGroupService.save(this.countryGroup)
        .pipe(
          catchError((error) => {
            return throwError(() => new Error(this.exceptionsService.formatHttpError(error)))
          })
        )
        .subscribe({
          next: data => {
            this.alertColourProgress=AlertClassColour.Green;
            this.countryGroupDataHasBeenSaved.emit();
            this.saveProcessComplete();
          },
          error: error => {
            this.alertColourProgress=AlertClassColour.Red;
            this.exceptionsService.handleException(new Error(error));
          }
        });
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private saveProcessComplete() {
    try {
      this.completedModalForm.openForm(this.countryGroupSavedMessage, "");
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  //#endregion

  //#region Country picker

  private loadCountries(): void {
    try {
      this.allCountries = [];
      this.selected     = new Set<number>();

      if (this.countryGroup != undefined)
        this.countryGroup.allocations = [];

      this.countryGroupService.getAllCountriesForGroupId(this.countryGroupId).subscribe({
        next: (rows: CountryGroupAllocationDTO[]) => {
          this.allCountries = (rows ?? []).sort((a, b) => (a.countryName ?? '').localeCompare(b.countryName ?? ''));
          this.selected     = new Set<number>(this.allCountries.filter(c => c.included == true).map(c => c.countryId));
        },
        error: (e) => this.exceptionsService.handleException(<Error>e)
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  get filteredCountries(): CountryGroupAllocationDTO[] {
    const q = this.countrySearch.toLowerCase().trim();
    if (q == '')
      return this.allCountries;

    return this.allCountries.filter(c => (c.countryName ?? '').toLowerCase().includes(q));
  }

  get allFilteredSelected(): boolean {
    const list = this.filteredCountries;
    return list.length > 0 && list.every(c => this.selected.has(c.countryId));
  }

  public isSelected(country: CountryGroupAllocationDTO): boolean {
    return this.selected.has(country.countryId);
  }

  public toggleCountry(country: CountryGroupAllocationDTO) {
    try {
      if (this.readOnly)
        return;

      const included = !this.selected.has(country.countryId);
      this.setCountrySelected(country, included);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public selectAllCountries() {
    try {
      if (this.readOnly)
        return;

      this.filteredCountries.forEach(c => { if (!this.selected.has(c.countryId)) this.setCountrySelected(c, true); });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public clearAllCountries() {
    try {
      if (this.readOnly)
        return;

      this.allCountries.forEach(c => { if (this.selected.has(c.countryId)) this.setCountrySelected(c, false); });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private setCountrySelected(country: CountryGroupAllocationDTO, included: boolean) {
    if (included)
      this.selected.add(country.countryId);
    else
      this.selected.delete(country.countryId);

    country.included = included;
    country.excluded = !included;
    country.changed  = true;

    this.upsertAllocation(country, included);
  }

  private upsertAllocation(row: any, included: boolean) {
    let index = this.countryGroup?.allocations.findIndex(item => item.countryId === row.countryId);

    if (index == -1 || index == undefined) {
      const newItem: CountryGroupAllocationDTO = {
        active          : true,
        changed         : true,
        companyID       : row.companyID,
        countryFlagIcon : row.countryFlagIcon,
        countryId       : row.countryId,
        countryName     : row.countryName,
        enteredBy       : this.curUser.userId,
        excluded        : !included,
        groupId         : this.countryGroupId,
        id              : row.id,
        included        : included,
        groupName       : "",
        systemOwned     : false,
      };
      this.countryGroup?.allocations.push(newItem);
    } else {
      this.countryGroup!.allocations[index].changed  = true;
      this.countryGroup!.allocations[index].excluded = !included;
      this.countryGroup!.allocations[index].included = included;
    }
  }

  //#endregion

  public completedModalHasBeenClosed() {
    try {
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
}
